import React from 'react';
import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import type { BlastRadiusScoreDocument } from '../../api-utils/blast';
import type { EntityRiskScoreDocument } from '../../api-utils/entity_risk';
import type { ThreatEventItem } from '../../api-utils/threats';
import type { ForceGraphNode } from './correlationGraph';
import IncidentHistory from './IncidentHistory';
import AttackPathStepper from './AttackPathStepper';
import type { SecurityEventItem } from '../../api-utils/security_events';
import {
  driverSignals,
  ebrBars,
  ebrScoreOf,
  eriBars,
  eriFormula,
  hasEriData,
  titleCase,
} from './dossierModel';
import {
  formatClock,
  formatDate,
  scoreCls,
  scoreColor,
  toScoreNumber,
  trendClass,
} from './eriUtils';

export interface CampaignDossierMember {
  actionId: string;
  threatEventId: string;
  label: string;
  step: number;
  firstEventTime?: string;
  lastEventTime?: string;
  riskLevel?: string;
}

interface NodeDossierProps {
  node: ForceGraphNode;
  /** False when the dialog around it already names the node. */
  showTitle?: boolean;
  eri?: EntityRiskScoreDocument;
  ebr?: BlastRadiusScoreDocument;
  scopedThreatEvent?: ThreatEventItem | null;
  scopedSecurityEvents?: SecurityEventItem[];
  /** Member threats for campaign dossier History (chronological merge). */
  campaignThreats?: ThreatEventItem[];
  campaignMembers?: CampaignDossierMember[];
  threatLabels?: Record<string, string>;
  attackPathStepNotes?: Record<string, string>;
  attackPathFiltered?: boolean;
  onOpenIncident: (id: string) => void;
  /** Absent when the entity has no dossier to open. */
  onOpenEntity?: (name: string) => void;
  /** Makes this entity the scope of the graph. Absent when it already is, or for a threat node. */
  onFocusEntity?: () => void;
  onOpenEbr?: () => void;
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="c3-dossier-k">{label}</div>
      <div className={`break-all ${mono ? 'font-mono text-[12px]' : ''}`}>{value}</div>
    </div>
  );
}

function ScoreRing({ score }: { score: number }) {
  const clamped = Math.max(0, Math.min(100, score));
  return (
    <div
      className="c3-dossier-ring"
      style={{
        background: `conic-gradient(${scoreColor(clamped)} ${clamped * 3.6}deg, var(--line-2) 0deg)`,
      }}
    >
      <div className="c3-dossier-ring-inner">
        <span className={`c3-dossier-ring-score ${scoreCls(clamped)}`}>{Math.round(clamped)}</span>
      </div>
    </div>
  );
}

function BarRow({
  bars,
}: {
  bars: Array<{ code: string; component: number }>;
}) {
  return (
    <div className="c3-dossier-bars">
      {bars.map((bar) => (
        <div key={bar.code} className="c3-dossier-bar">
          <div className="c3-dossier-bar-meta">
            <span>{bar.code}</span>
            <span>{Math.round(Math.max(0, Math.min(100, bar.component)))}</span>
          </div>
          <div className="c3-dossier-bar-track">
            <i style={{ width: `${Math.max(0, Math.min(100, bar.component))}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

const NodeDossier: React.FC<NodeDossierProps> = ({
  node,
  showTitle = true,
  eri,
  ebr,
  scopedThreatEvent,
  scopedSecurityEvents = [],
  campaignThreats,
  campaignMembers,
  threatLabels,
  attackPathStepNotes,
  attackPathFiltered,
  onOpenIncident,
  onOpenEntity,
  onFocusEntity,
  onOpenEbr,
}) => {
  const isCampaign =
    String(node.universalType || '').toLowerCase() === 'campaign' ||
    Boolean(campaignMembers && campaignMembers.length > 0 && campaignThreats);

  const showEri = node.layer === 'infrastructure' && hasEriData(eri);
  const ebrScore = node.layer === 'infrastructure' ? ebrScoreOf(ebr) : null;
  const showEbr = ebrScore != null;
  const kicker = titleCase(node.universalDomain || node.layer || 'Entity');
  const typeLine = [
    titleCase(node.layer || ''),
    titleCase(node.universalDomain),
    titleCase(node.universalType),
    titleCase(node.provider),
    `${node.linkCount} edge${node.linkCount === 1 ? '' : 's'}`,
  ]
    .filter(Boolean)
    .join(' · ');
  const providerRef = [node.provider, node.externalId].filter(Boolean).join(':');
  const firstSeen = scopedThreatEvent?.first_event_time || scopedThreatEvent?.created_at;
  const lastActivity = eri?.computed_at || ebr?.computed_at || scopedThreatEvent?.last_event_time;
  const eriScore = showEri && eri ? toScoreNumber(eri.score) ?? 0 : 0;
  const eriDelta = showEri && eri ? toScoreNumber(eri.score_delta_7d) : null;
  const eriPrev = showEri && eri ? toScoreNumber(eri.previous_score) : null;
  const eriDriver = showEri && eri ? driverSignals(eri) : null;
  const eriBarRows = showEri && eri ? eriBars(eri) : [];
  const eriMath = showEri && eri ? eriFormula(eri) : null;

  const historyThreats =
    campaignThreats && campaignThreats.length > 0 ? campaignThreats : undefined;
  const showHistory = Boolean(scopedThreatEvent || (historyThreats && historyThreats.length > 0));

  return (
    <>
      <div className="c3-dossier-head">
        <div className="c3-dossier-kicker">
          <span>{kicker}</span>
          {providerRef ? <span className="c3-dossier-ref">{providerRef}</span> : null}
        </div>
        {showTitle ? <div className="c3-dossier-title">{node.label}</div> : null}
        <div className="c3-dossier-type">{typeLine}</div>
      </div>

      {showEri && eri ? (
        <section className="c3-dossier-block">
          <div className="c3-dossier-section">
            <span className="c3-dossier-section-label">Risk</span>
            <span className="c3-dossier-quiet">last computed {formatClock(eri.computed_at)}</span>
          </div>
          <div className="c3-dossier-risk">
            <ScoreRing score={eriScore} />
            <div className="c3-dossier-risk-copy">
              {eriDelta != null ? (
                <div className={`c3-dossier-trend ${trendClass(eriDelta)}`}>
                  {eriDelta > 0 ? (
                    <TrendingUp size={12} strokeWidth={2} aria-hidden className="inline -mt-0.5 mr-1" />
                  ) : eriDelta < 0 ? (
                    <TrendingDown size={12} strokeWidth={2} aria-hidden className="inline -mt-0.5 mr-1" />
                  ) : (
                    <Minus size={12} strokeWidth={2} aria-hidden className="inline -mt-0.5 mr-1" />
                  )}
                  {Math.abs(Math.round(eriDelta))} over 7d
                  {eriPrev != null ? ` · ${Math.round(eriPrev)} → ${Math.round(eriScore)}` : ''}
                </div>
              ) : null}
              {eriDriver ? (
                <>
                  <div className="c3-dossier-driver">
                    <span className="c3-dossier-section-label">Top driver</span>
                    {eriDriver.label}
                  </div>
                  <div className="c3-dossier-quiet">
                    · {eriDriver.signals} signal{eriDriver.signals === 1 ? '' : 's'}
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {eriMath ? (
            <pre className="c3-dossier-formula">
              {eriBarRows
                .map(
                  (bar) =>
                    `${bar.code}  ${bar.weight.toFixed(2).replace(/^0/, '')} × ${Math.round(
                      bar.component
                    )} = ${bar.product.toFixed(1)}`
                )
                .join('\n')}
              {`\nΣ ${(eriMath.intensity * 100).toFixed(1)} × stake ${eriMath.amp.toFixed(2)} = ${eriMath.raw.toFixed(
                1
              )} → ${Math.round(eriMath.score)}`}
              {'\nscore = intensity × (0.40 + 0.60 × at_stake)'}
            </pre>
          ) : null}
          <BarRow bars={eriBarRows} />
        </section>
      ) : null}

      <section className="c3-dossier-facts">
        <DetailRow label="ID" value={node.id} mono />
        {scopedThreatEvent && (
          <>
            <DetailRow
              label={isCampaign ? 'Campaign window' : 'Linked threat'}
              value={
                isCampaign
                  ? `${scopedThreatEvent.first_event_time || '—'} → ${
                      scopedThreatEvent.last_event_time || '—'
                    }`
                  : scopedThreatEvent.detection_classification || scopedThreatEvent._id
              }
            />
            {!isCampaign ? (
              <DetailRow
                label="Window"
                value={`${scopedThreatEvent.first_event_time || '—'} → ${
                  scopedThreatEvent.last_event_time || '—'
                }`}
              />
            ) : (
              <DetailRow
                label="Members"
                value={`${campaignMembers?.length ?? 0} action${
                  (campaignMembers?.length ?? 0) === 1 ? '' : 's'
                } · ${scopedSecurityEvents.length} event${
                  scopedSecurityEvents.length === 1 ? '' : 's'
                }`}
              />
            )}
            {attackPathFiltered ? (
              <DetailRow label="Attack path" value={`filtered to ${node.label}`} />
            ) : null}
          </>
        )}
      </section>

      {(firstSeen || lastActivity) && (
        <section className="c3-dossier-block">
          <div className="c3-dossier-section">
            <span className="c3-dossier-section-label">Activity</span>
          </div>
          {firstSeen ? (
            <div className="c3-dossier-activity">
              <span>First seen</span>
              <span>{formatDate(firstSeen)}</span>
            </div>
          ) : null}
          {lastActivity ? (
            <div className="c3-dossier-activity">
              <span>Last activity</span>
              <span>{formatClock(lastActivity)}</span>
            </div>
          ) : null}
        </section>
      )}

      {isCampaign && campaignMembers && campaignMembers.length > 0 ? (
        <section className="c3-dossier-block">
          <div className="c3-dossier-section">
            <span className="c3-dossier-section-label">Actions</span>
            <span className="c3-dossier-quiet">chronological</span>
          </div>
          <ol className="c3-dossier-members">
            {campaignMembers.map((member) => (
              <li key={member.actionId}>
                <button
                  type="button"
                  className="c3-dossier-member"
                  onClick={() => onOpenIncident(member.threatEventId)}
                  title="Open member incident"
                >
                  <span className="c3-dossier-member-idx">{member.step}</span>
                  <span className="c3-dossier-member-copy">
                    <span className="c3-dossier-member-label">{member.label}</span>
                    <span className="c3-dossier-quiet">
                      {[
                        member.riskLevel,
                        member.firstEventTime
                          ? `${formatClock(member.firstEventTime)}${
                              member.lastEventTime && member.lastEventTime !== member.firstEventTime
                                ? ` → ${formatClock(member.lastEventTime)}`
                                : ''
                            }`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {isCampaign && scopedThreatEvent ? (
        <section className="c3-dossier-block">
          <div className="c3-dossier-section">
            <span className="c3-dossier-section-label">Attack path</span>
            <span className="c3-dossier-quiet">all member events</span>
          </div>
          <div className="c3-dossier-attack">
            <AttackPathStepper
              threat={scopedThreatEvent}
              events={scopedSecurityEvents}
              stepNotes={attackPathStepNotes}
              emptyHint="No security events on campaign members yet."
            />
          </div>
        </section>
      ) : null}

      {scopedThreatEvent && !isCampaign ? (
        <button type="button" className="c3-pill mt-2" onClick={() => onOpenIncident(scopedThreatEvent._id)}>
          Remediate
        </button>
      ) : node.layer !== 'threat' ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {onOpenEntity ? (
            <button type="button" className="c3-pill" onClick={() => onOpenEntity(node.label)}>
              Open entity
            </button>
          ) : null}
          {onFocusEntity ? (
            <button
              type="button"
              className="c3-pill"
              onClick={onFocusEntity}
              title="Show only this entity and what is around it"
            >
              Focus here
            </button>
          ) : null}
        </div>
      ) : null}

      {showHistory ? (
        <div className="c3-dossier-history">
          <div className="c3-dossier-k">History</div>
          <IncidentHistory
            threat={historyThreats ? null : scopedThreatEvent}
            threats={historyThreats}
            events={scopedSecurityEvents}
            threatLabels={threatLabels}
          />
        </div>
      ) : null}

      {showEbr && ebr ? (
        <section className="c3-dossier-ebr mt-auto">
          <button
            type="button"
            className="c3-dossier-ebr-open"
            onClick={() => onOpenEbr?.()}
            disabled={!onOpenEbr}
            title={onOpenEbr ? 'Open Effective Blast Radius detail' : undefined}
          >
            <div className="c3-dossier-section">
              <span className="c3-dossier-section-label">EBR</span>
              <span className="c3-dossier-quiet">last computed {formatClock(ebr.computed_at)}</span>
            </div>
            <div className="c3-dossier-ebr-row">
              <span className={`c3-dossier-ebr-score ${scoreCls(ebrScore ?? 0)}`}>
                {Math.round(ebrScore ?? 0)}
              </span>
              <div className="c3-dossier-quiet">
                {[ebr.band, ebr.max_domain ? `max ${ebr.max_domain}` : '', ebr.reachability_count != null ? `reach ${ebr.reachability_count}` : '']
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <BarRow bars={ebrBars(ebr)} />
          </button>
        </section>
      ) : null}
    </>
  );
};

export default NodeDossier;
